import {
  WppAccordion,
  WppDivider,
  WppIconClose,
  WppInput,
  WppProgressIndicator,
  WppSpinner,
  WppTooltip,
} from '@wppopen/components-library-react'
import { AnalyticsActionType } from '@wppopen/core'
import clsx from 'clsx'
import { useEffect, useState } from 'react'

import { usePostProjectSyncPreview } from 'api/querries/usePostSyncPreview'
import InfoIcon from 'assets/icons/info.svg'
import SuccessIcon from 'assets/icons/success.svg'
import WarningIcon from 'assets/icons/warning.svg'
import { Button } from 'components/common/button/Button'
import { SyncAction } from 'types/Project'
import { UhubChanges } from 'types/UhubChanges'
import { ANALYTICS_EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './WarningBanner.module.scss'
interface Props {
  id: string
  onSyncAction?: (id: string, actionType: SyncAction) => void
  isPending?: boolean
  projectName: string
  onSetNewUhubId: (newId: string) => void
  onSetNewUhubPreview: (newPreview: UhubChanges) => void
}

const WarningBanner = ({ id, onSyncAction, isPending, projectName, onSetNewUhubId, onSetNewUhubPreview }: Props) => {
  const handleProjectSync = (actionType: SyncAction) => {
    onSyncAction?.(id, actionType)
  }
  const {
    mutateAsync: checkNewUhubId,
    data: uhubPreview,
    isError,
    isPending: isLoading,
    reset,
  } = usePostProjectSyncPreview()

  const [newUhubId, setNewUhubId] = useState<string>('')
  const [fullTooltipMessage, setFullTooltipMessage] = useState<string>('')
  function checkJiraId() {
    checkNewUhubId({ id, actionType: SyncAction.ACCEPT, uhubId: newUhubId }).catch(error => console.error(error))
  }
  useEffect(() => {
    onSetNewUhubPreview(uhubPreview?.data as UhubChanges)
    if (!newUhubId?.length) {
      reset()
      setFullTooltipMessage('')
    }
    if (uhubPreview) {
      onSetNewUhubId(newUhubId)
    }
    if (uhubPreview) {
      onSetNewUhubPreview(uhubPreview.data as UhubChanges)
    }
    if (isError) {
      setFullTooltipMessage('Incorrect ID, please select another one.')
    }
    const changes = (uhubPreview?.data as UhubChanges)?.changes
    if (changes?.length === 1) {
      setFullTooltipMessage(`This field will be changed: ${changes?.map(change => change.fieldLabel).join(', ')}`)
    }
    if (changes?.length) {
      const fieldsString = changes?.map(change => change.fieldLabel).join(', ')
      setFullTooltipMessage(`These fields will be changed: ${fieldsString}`)
    }
  }, [uhubPreview, newUhubId, onSetNewUhubId, onSetNewUhubPreview, isError, reset])

  function getMessageType() {
    if (isError) {
      return 'error'
    }
    if ((uhubPreview?.data as UhubChanges)?.changes.length) {
      return 'warning'
    }
  }
  return (
    <div className={clsx(styles.WarningBanner, 'flex flex-col gap-6', !fullTooltipMessage.length && 'hide-tooltip')}>
      <div className="flex gap-3 items-start">
        <div className="flex items-center justify-center">
          {isPending ? (
            <WppSpinner color="#191919" className="h-7 w-7" />
          ) : (
            <img src={WarningIcon} alt="warning-icon" className="h-7 w-7" />
          )}
        </div>
        <div>
          <p className={styles.title}>Brief Changed. Do you want to accept it?</p>
          <p className={styles.text}>
            There is a new updated version on Workfront that is not synced to UHUB. Do you want to accept the change?
          </p>
        </div>
      </div>

      <div className="flex gap-4 items-bottom">
        <WppAccordion size="m" className="grow" style={{ '--wpp-icon-color': 'black' }}>
          <span slot="header" className="leading-6 font-semibold">
            Switch to another UHUB ID
          </span>
          <div className="flex flex-col mt-6">
            <h4 className="font-bold text-14 leading-4">
              Please add the UHUB ID to connect the projects instead of creating a new one.
            </h4>
            <p className="text-14 leading-4 mt-2">
              If you don't add this ID, a new project will be created automatically and to link them you will need to do
              it directly in UHUB.
            </p>
            <div className="w-[363px] flex justify-between mt-6 mb-1">
              <div className="flex font-bold text-14 leading-[22px] text-[#4C4C4C] items-center gap-1">
                UHUB ID
                <img src={InfoIcon} alt="info-icon" className="h-4 w-4" />
              </div>
              <a
                href={process.env.REACT_APP_UHUB_URL}
                className="font-bold text-14 leading-[22px] text-[#383838] underline"
                target="_blank"
                rel="noreferrer"
              >
                Go to Jira to find the ID
              </a>
            </div>

            <WppTooltip config={{ placement: 'bottom' }} text={fullTooltipMessage} className="w-full h-[70px]">
              <div className="w-[363px] relative">
                <WppInput
                  className={clsx('w-full h-[70px]', getMessageType() === 'warning' && 'is-warning')}
                  name="search"
                  placeholder="ID"
                  value={newUhubId}
                  message={
                    fullTooltipMessage.length > 100 ? `${fullTooltipMessage.substring(0, 100)}...` : fullTooltipMessage
                  }
                  messageType={getMessageType()}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      checkJiraId()
                    }
                  }}
                  style={{ '--text-input-border-color': '#8B8B8B' }}
                  onWppChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewUhubId(e?.target?.value)}
                />

                {!isLoading && uhubPreview && (
                  <img className="max-w-[16px] absolute left-36 top-3 cursor-pointer" src={SuccessIcon} alt="success" />
                )}

                <div className="absolute right-2 top-0 flex gap-3 h-[40px] items-center">
                  <WppDivider
                    vertical={true}
                    style={{ '--wpp-divider-bg-color': '#E1E1E1', '--wpp-divider-height': '24px' }}
                  />
                  {!isLoading && uhubPreview && (
                    <WppIconClose
                      className="me-2"
                      role="button"
                      onClick={() => {
                        reset()
                        setNewUhubId('')
                      }}
                    />
                  )}

                  {isLoading && <WppProgressIndicator variant="circle" width={14} />}
                  {!isLoading && !uhubPreview && (
                    <button
                      disabled={!newUhubId.length}
                      className="font-bold text-14 leading-[22px] text-[#383838] disabled:text-[#8B8B8B]"
                      onClick={checkJiraId}
                    >
                      Check ID
                    </button>
                  )}
                </div>
              </div>
            </WppTooltip>
          </div>
        </WppAccordion>
        <div className="flex gap-3 shrink-0 items-end">
          <Button
            disabled={isPending}
            text="Reject"
            className={clsx(styles.btnAction, styles.btnReject)}
            onClick={() => {
              handleProjectSync(SyncAction.REJECT)
              trackAnalytics({
                type: AnalyticsActionType.action,
                payload: {
                  action: ANALYTICS_EVENTS.DETAILED_ITEM_STATUS_ACTION_CLICKED(projectName),
                  params: [{ key: 'actionType', value: SyncAction.REJECT }],
                },
              })
            }}
          />
          <Button
            disabled={isPending}
            text="Accept"
            className={clsx(styles.btnAction, styles.btnAccept)}
            onClick={() => {
              handleProjectSync(SyncAction.ACCEPT)
              trackAnalytics({
                type: AnalyticsActionType.action,
                payload: {
                  action: ANALYTICS_EVENTS.DETAILED_ITEM_STATUS_ACTION_CLICKED(projectName),
                  params: [{ key: 'actionType', value: SyncAction.ACCEPT }],
                },
              })
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default WarningBanner
